import pegasus from "../../assets/scripts/global/01-pegasus";

/**
 * allModals
 *
 * Component code for creating all modals on the page
 *
 * 	Usage:
 * 	Add link with the attr 'data-pegasus-modal'.
 * 	Any values passed to the attr will be added as classes on the modal so it can be styled accordingly.
 * 	To include modal navigation, pass the class 'Modal--with-nav' to the data attr. This class will be picked up and the nav will be built.
 *
 * 	Example:
 *  <a href="#" data-pegasus-modal="Modal--team Modal--with-nav"></a>
 *
 * @since  2.0.0
 */
window.allModals = () => {
    return {
        init() {
            const _this = this;
            const modalSkeleton = pegasus.stringToHTML(
                document.getElementById("Modal__Skeleton").innerHTML
            );
            const modalLinks = _this.createModalLinks(
                document.querySelectorAll("[data-pegasus-modal]")
            );

            modalLinks.forEach((link, index) => {
                const modal = modalSkeleton.cloneNode(true);

                let cssClass = "Modal",
                    modalNav;

                //check for classes to add
                if (link.dataset.pegasusModal.length) {
                    cssClass += " " + link.dataset.pegasusModal;

                    //if nav specified
                    if (link.dataset.pegasusModal.includes("Modal--with-nav")) {
                        modalNav = _this.buildModalNav(index, modalLinks);

                        if (modalNav) {
                            modal
                                .querySelector(".Modal__navigation")
                                .append(modalNav);
                        }
                    }
                }

                //setup classes and id
                modal.className = cssClass;
                modal.id = link.dataset.pegasusModalId;

                //append modal template to page if it does nt already exist
                if (!document.getElementById(link.dataset.pegasusModalId)) {
                    document.body.append(modal);
                }
            });

            window.modal = this;
        },

        doesLinkAlreadyExist(link, linksArray) {
            return linksArray.find((element) => element.href === link.href);
        },

        createModalLinks(links) {
            const _this = this;
            const modalLinks = [];

            if (links.length) {
                links.forEach((link, index) => {
                    // Add ?modal=yes query string to url, so head and foot are removed from fetched page
                    const href = new URL(link.href);

                    link.href = href;

                    const existingLink = _this.doesLinkAlreadyExist(
                        link,
                        modalLinks
                    );

                    //check for existing link and copy id if true
                    if (existingLink) {
                        link.setAttribute(
                            "data-pegasus-modal-id",
                            existingLink.dataset.pegasusModalId
                        );
                    } else {
                        link.setAttribute(
                            "data-pegasus-modal-id",
                            `modal-${index}`
                        );
                    }

                    link.addEventListener("click", (e) => {
                        e.preventDefault();
                        if(link.dataset.pegasusModal.includes("Modal--Form")) {
                            _this.loadFormModalContent(link.dataset.pegasusFormId, link.dataset.pegasusModalId);
                        } else {
                            _this.loadModalContent(
                                link.href,
                                link.dataset.pegasusModalId
                            );
                        }
                    });

                    //add event to emit 'modalopen' event for specified modal
                    link.addEventListener("click", _this.emitModalOpen);

                    //check we're not adding existing links.
                    if (!existingLink) modalLinks.push(link);
                });
            }
            return modalLinks;
        },

        emitModalOpen(e) {
            e.preventDefault();
            const modalID = e.currentTarget.dataset.pegasusModalId;
            const myEvent = new CustomEvent("modalopen", {
                detail: { modalID },
            });
            window.dispatchEvent(myEvent);
        },

        // This method executes all inline <script> tags contained within the specified container.
        executeInlineScripts(container) {
            const scripts = container.querySelectorAll("script");
            scripts.forEach((script) => {
                const newScript = document.createElement("script");
                if (script.src) {
                    newScript.src = script.src;
                } else {
                    newScript.textContent = script.textContent;
                }

                document.body.appendChild(newScript);
                document.body.removeChild(newScript);
            });
        },

        // This method asynchronously loads content for the modal, injects it into the modal body,
        // executes any inline scripts within the new content, and triggers Gravity Forms initialization.
        loadModalContent(href, modalID) {


            const url = new URL(href);
            url.searchParams.append("modal", "yes");

            pegasus.fetchContent(url)
                .then((resp) => {
                    if (!resp.ok) {
                        throw new Error(resp.statusText);
                    }
                    console.log("Loading modal content...");
                    return resp.text();
                })
                .then((htmlResponse) => {
                    const modalBody = document.querySelector(`#${modalID} .Modal__body`);
                    if (!modalBody) {
                        throw new Error(`Modal body not found for modal ID: ${modalID}`);
                    }
                    // Inject the retrieved HTML content into the modal body.
                    modalBody.innerHTML = htmlResponse;
                    
                    // Execute any inline <script> tags that were embedded in the fetched HTML.
                    this.executeInlineScripts(modalBody);
                    return modalBody;
                })
                .then((modalBody) => {
                    // Dispatch Gravity Forms' post-render event using jQuery so that internal event bindings are re-established.
                    if (window.jQuery) {
                        const formElement = modalBody.querySelector("form");
                        if (formElement) {
                            const idMatch = formElement.id.match(/gform_(\d+)/);
                            if (idMatch) {
                                const formId = idMatch[1];
                                window.jQuery(document).trigger("gform_post_render", [formId, 1]);
                                console.log("Triggered gform_post_render for form id " + formId);
                            } else {
                                console.warn("Form ID not found in the form element's id.");
                            }
                        } else {
                            console.warn("No form element found in modal body.");
                        }
                    } else {
                        console.warn("jQuery is not available. Gravity Forms requires jQuery.");
                    }
                })
                .catch((error) => {
                    console.error(error);
                    const modalBody = document.querySelector(`#${modalID} .Modal__body`);
                    if (modalBody) {
                        const errorContainer = modalBody.querySelector(".Modal__error");
                        if (errorContainer) {
                            errorContainer.classList.remove("hidden");
                        }
                    }
                })
                .finally(() => {
                    if (document.lazyLoadInstance) {
                        document.lazyLoadInstance.update();
                    }
                    window.dispatchEvent(new Event("resize"));
                });
        },

        loadFormModalContent(modalId, modalID){
            const urlSearchParams = new URLSearchParams({
                    action: "load_form_modal_content",
                    form_id: modalId,
                });
            pegasus.fetchContent(ajaxurl + '?' + urlSearchParams.toString()).then((resp) => {
                    if (!resp.ok) {
                        throw new Error(resp.statusText);
                    }
                    console.log("Loading modal content...");
                    return resp.text();
                })
                .then((htmlResponse) => {
                    const modalBody = document.querySelector(`#${modalID} .Modal__body`);
                    if (!modalBody) {
                        throw new Error(`Modal body not found for modal ID: ${modalID}`);
                    }
                    // Inject the retrieved HTML content into the modal body.
                    modalBody.innerHTML = htmlResponse;
                    
                    // Execute any inline <script> tags that were embedded in the fetched HTML.
                    this.executeInlineScripts(modalBody);
                    return modalBody;
                })
                .then((modalBody) => {
                    // Dispatch Gravity Forms' post-render event using jQuery so that internal event bindings are re-established.
                    if (window.jQuery) {
                        const formElement = modalBody.querySelector("form");
                        if (formElement) {
                            const idMatch = formElement.id.match(/gform_(\d+)/);
                            if (idMatch) {
                                const formId = idMatch[1];
                                window.jQuery(document).trigger("gform_post_render", [formId, 1]);
                                console.log("Triggered gform_post_render for form id " + formId);
                            } else {
                                console.warn("Form ID not found in the form element's id.");
                            }
                        } else {
                            console.warn("No form element found in modal body.");
                        }
                    } else {
                        console.warn("jQuery is not available. Gravity Forms requires jQuery.");
                    }
                })
                .catch((error) => {
                    console.error(error);
                    const modalBody = document.querySelector(`#${modalID} .Modal__body`);
                    if (modalBody) {
                        const errorContainer = modalBody.querySelector(".Modal__error");
                        if (errorContainer) {
                            errorContainer.classList.remove("hidden");
                        }
                    }
                })
                .finally(() => {
                    if (document.lazyLoadInstance) {
                        document.lazyLoadInstance.update();
                    }
                    window.dispatchEvent(new Event("resize"));
                });
        }
    };
};

// Scroll to top of modal body when navigating to a new page in the modal
if (window.jQuery) {
    window.jQuery(document).on("gform_page_loaded", function (event, formId, currentPage) {
        if (formId === 1) {
        const modalBodies = document.querySelectorAll(".Modal__body");
        modalBodies.forEach((body) => {
            body.scrollTop = 0;
        });
        }
    });
};  

window.singleModal = () => {
    return {
        open: false,
        handleModalOpen(evt) {
            if (this.$el.classList.contains("ModalSkeleton")) return;
            document.body.classList.add("modal-open");
            this.open = this.$el.id === evt.detail.modalID;
        },
        handleModalClose() {
            document.body.classList.remove("modal-open");
            this.open = false;
        },
    };
};
